import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "loginInput", "submitButton",
    "codeField", "codeInput",
    "registrationFields", "emailInput", "phoneInput",
    "firstNameInput", "lastNameInput",
    "loginStep", "message", "challengeToken", "registrationToken", "rememberInput"
  ]

  static values = {
    sendOtpUrl: String,
    verifyOtpUrl: String,
    registrationUrl: String,
    sendCodeText: String,
    verifyCodeText: String,
    signUpText: String,
    checkingText: String,
    codeSentText: String,
    codeVerifiedText: String,
    invalidLoginText: String,
    missingChallengeText: { type: String, default: "Please request a new code." },
    enterCodeText: { type: String, default: "Enter the code you received." },
    unexpectedText: { type: String, default: "Unexpected response. Please try again." },
    customerPermalink: { type: String, default: "" },
    returnTo: { type: String, default: "" },
    reloadOnSuccess: { type: Boolean, default: false }
  }

  connect() {
    this.state = "login"
    this.challengeToken = null
    this.registrationToken = null
    this.setButtonText(this.sendCodeTextValue)
    this.showMessage("")
  }

  async next(event) {
    event.preventDefault()

    if (this.state === "login") return this.sendCode()
    if (this.state === "code") return this.verifyCode()
    if (this.state === "registration") return this.completeRegistration()
  }

  async sendCode() {
    const login = this.loginInputTarget.value.trim()
    if (!login) {
      this.loginInputTarget.classList.add("input-error")
      this.showError(this.invalidLoginTextValue)
      return
    }

    this.loginInputTarget.classList.remove("input-error")
    this.showMessage("")
    this.setLoading(true, this.checkingTextValue)

    try {
      const payload = { login }
      if (this.customerPermalinkValue) payload.customer_permalink = this.customerPermalinkValue
      if (this.returnToValue) payload.return_to = this.returnToValue

      const response = await fetch(this.sendOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(payload)
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Unable to send code.")

      this.challengeToken = data.challenge_token
      if (this.hasChallengeTokenTarget) this.challengeTokenTarget.value = data.challenge_token

      this.state = "code"
      this.showCodeStep()
      this.showMessage(this.codeSentTextValue, "success")
      this.setButtonText(this.verifyCodeTextValue)
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.setLoading(false, this.state === "code" ? this.verifyCodeTextValue : this.sendCodeTextValue)
    }
  }

  async verifyCode() {
    if (!this.challengeToken) {
      this.showError(this.missingChallengeTextValue)
      return
    }

    const code = this.codeInputTarget.value.trim()
    if (!code) {
      this.showError(this.enterCodeTextValue)
      return
    }

    this.setLoading(true, this.checkingTextValue)

    try {
      const response = await fetch(this.verifyOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ challenge_token: this.challengeToken, code, remember_me: this.rememberDevice })
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Invalid code.")

      if (data.status === "signed_in") {
        this.finish(data.redirect_path)
        return
      }

      if (data.status === "needs_registration") {
        this.registrationToken = data.registration_token
        if (this.hasRegistrationTokenTarget) this.registrationTokenTarget.value = data.registration_token
        this.showRegistration(data.login, data.login_type)
        this.showMessage(this.codeVerifiedTextValue, "success")
        this.setButtonText(this.signUpTextValue)
        return
      }

      this.showError(this.unexpectedTextValue)
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.setLoading(false, this.state === "registration" ? this.signUpTextValue : this.verifyCodeTextValue)
    }
  }

  async completeRegistration() {
    if (!this.registrationToken) {
      this.showError(this.missingChallengeTextValue)
      return
    }

    this.setLoading(true, this.checkingTextValue)

    const payload = {
      registration_token: this.registrationToken,
      remember_me: this.rememberDevice,
      user: {
        first_name: this.firstNameInputTarget.value,
        last_name: this.lastNameInputTarget.value,
        email: this.hasEmailInputTarget ? this.emailInputTarget.value : undefined,
        phone_number: this.hasPhoneInputTarget ? this.phoneInputTarget.value : undefined
      }
    }

    try {
      const response = await fetch(this.registrationUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(payload)
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Could not create the account.")

      if (data.status === "registered") {
        this.finish(data.redirect_path)
        return
      }

      this.showError(this.unexpectedTextValue)
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.setLoading(false, this.signUpTextValue)
    }
  }

  finish(redirectPath) {
    if (this.reloadOnSuccessValue) {
      window.location.reload()
    } else {
      window.location = redirectPath || "/"
    }
  }

  showCodeStep() {
    this.loginStepTarget.classList.add("opacity-60")
    this.loginInputTarget.disabled = true

    if (this.hasCodeFieldTarget) {
      this.codeFieldTarget.classList.remove("hidden")
    }

    if (this.hasCodeInputTarget) {
      this.codeInputTarget.focus()
    }
  }

  showRegistration(login, loginType) {
    this.state = "registration"

    if (this.hasRegistrationFieldsTarget) {
      this.registrationFieldsTarget.classList.remove("hidden")
      this.registrationFieldsTarget.querySelectorAll("input").forEach(i => (i.disabled = false))
    }

    if (loginType === "email" && this.hasEmailInputTarget) {
      this.emailInputTarget.value = login
      this.emailInputTarget.disabled = true
    }

    if (loginType === "phone" && this.hasPhoneInputTarget) {
      this.phoneInputTarget.value = login
      this.phoneInputTarget.disabled = true
    }
  }

  setButtonText(text) {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.textContent = text
  }

  setLoading(isLoading, text) {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = isLoading
    if (text) this.setButtonText(text)
  }

  showMessage(text, tone = "info") {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text || ""

    this.messageTarget.classList.remove("text-error", "text-success")
    if (tone === "error") this.messageTarget.classList.add("text-error")
    if (tone === "success") this.messageTarget.classList.add("text-success")
  }

  showError(text) {
    this.showMessage(text, "error")
  }

  get rememberDevice() {
    return this.hasRememberInputTarget ? this.rememberInputTarget.checked : true
  }

  get headers() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }
}
